import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  TrendingUp, 
  DollarSign, 
  PieChart, 
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Banknote,
  Plus,
  CircleDollarSign
} from "lucide-react";

interface Investment {
  id: string;
  amount: number;
  investment_date: string;
  status: 'active' | 'completed' | 'pending';
  expected_return_rate: number;
  monthly_return: number;
}

interface Return {
  id: string;
  investment_id: string;
  return_type: 'interest' | 'principal';
  amount: number;
  return_date: string;
  status: 'pending_approval' | 'approved' | 'reinvested' | 'withdrawn' | 'calculated';
  is_calculated?: boolean;
}

interface CalculatedReturn {
  id: string;
  investment_id: string;
  return_type: 'interest';
  amount: number;
  return_date: string;
  status: 'calculated';
  is_calculated: boolean;
}

const InvestmentDashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [returns, setReturns] = useState<Return[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInterest, setShowInterest] = useState(true);
  const [showPrincipal, setShowPrincipal] = useState(true);

  const fetchInvestments = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('investments')
        .select('*')
        .eq('user_id', user.id)
        .order('investment_date', { ascending: false });

      if (error) {
        toast({
          title: "Erro ao carregar investimentos",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setInvestments(data || []);
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchReturns = async () => {
    if (!user) return;

    try {

      // Fetch actual returns from database
      const { data: actualReturns, error: returnsError } = await supabase
        .from('returns')
        .select('*')
        .eq('user_id', user.id)
        .order('return_date', { ascending: false });

      if (returnsError) {
        toast({
          title: "Erro ao carregar rendimentos",
          description: returnsError.message,
          variant: "destructive",
        });
        return;
      }

      

      // Fetch calculated returns from function - ONLY for current user's investments
      const { data: calculatedReturns, error: calculatedError } = await supabase
        .rpc('calculate_monthly_returns');

      if (calculatedError) {
        // Don't show error toast for calculated returns
      }

      

      // Filter calculated returns to only include current user's investments
      let userCalculatedReturns = [];
      if (calculatedReturns) {
        userCalculatedReturns = calculatedReturns.filter((calcReturn: any) => calcReturn.user_id === user.id);
        
      }

      // Combine actual and calculated returns, avoiding duplicates
      const allReturns: any[] = [...(actualReturns || [])];
      
      if (userCalculatedReturns && userCalculatedReturns.length > 0) {
        userCalculatedReturns.forEach((calcReturn: any) => {
          // Check if this calculated return already exists in actual returns
          const existsInActual = actualReturns?.some(actualReturn => 
            actualReturn.investment_id === calcReturn.investment_id &&
            actualReturn.return_type === calcReturn.return_type &&
            actualReturn.return_date === calcReturn.return_date
          );
          
          if (!existsInActual) {
            allReturns.push({
              id: `calc_${calcReturn.investment_id}_${calcReturn.return_type}_${calcReturn.return_date}`,
              investment_id: calcReturn.investment_id,
              return_type: calcReturn.return_type,
              amount: calcReturn.amount,
              return_date: calcReturn.return_date,
              status: 'pending_approval',
              is_calculated: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              user_id: user.id
            });
          }
        });
      }

      // Sort by return_date descending
      allReturns.sort((a, b) => new Date(b.return_date).getTime() - new Date(a.return_date).getTime());
      
      
      setReturns(allReturns);
    } catch (error) {
      // Handle error silently
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (user) {
        
        setLoading(true);
        // Clear any previous state to avoid cache issues
        setInvestments([]);
        setReturns([]);
        await Promise.all([fetchInvestments(), fetchReturns()]);
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const handleApproveReturn = async (returnId: string, action: 'reinvest' | 'withdraw') => {
    try {
      // Check if this is a calculated return (ID starts with "calc_")
      if (returnId.startsWith('calc_')) {
        // Find the calculated return in our local data
        const calculatedReturn = returns.find(ret => ret.id === returnId);
        if (!calculatedReturn) {
          toast({
            title: "Erro",
            description: "Retorno não encontrado.",
            variant: "destructive",
          });
          return;
        }

        // Create a new return record in the database
        const { data: newReturn, error: insertError } = await supabase
          .from('returns')
          .insert({
            user_id: user?.id,
            investment_id: calculatedReturn.investment_id,
            return_type: calculatedReturn.return_type,
            amount: calculatedReturn.amount,
            return_date: calculatedReturn.return_date,
            status: action === 'reinvest' ? 'reinvested' : 'withdrawn'
          })
          .select()
          .single();

        if (insertError) {
          toast({
            title: "Erro ao processar aprovação",
            description: insertError.message,
            variant: "destructive",
          });
          return;
        }
      } else {
        // Handle existing returns
        const { error } = await supabase
          .from('returns')
          .update({ status: action === 'reinvest' ? 'reinvested' : 'withdrawn' })
          .eq('id', returnId)
          .eq('user_id', user?.id);

        if (error) {
          toast({
            title: "Erro ao processar aprovação",
            description: error.message,
            variant: "destructive",
          });
          return;
        }
      }

      toast({
        title: "Aprovação processada!",
        description: `Rendimento ${action === 'reinvest' ? 'reinvestido' : 'sacado'} com sucesso.`,
      });

      // Refresh returns
      await fetchReturns();
    } catch (error) {
      toast({
        title: "Erro ao processar aprovação",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  // Calculate key metrics with corrected logic
  const totalInvested = investments.reduce((sum, inv) => sum + inv.amount, 0);
  const activeInvestments = investments.filter(inv => inv.status === 'active');
  const totalMonthlyReturn = activeInvestments.reduce((sum, inv) => sum + inv.monthly_return, 0);
  const pendingReturns = returns.filter(ret => ret.status === 'pending_approval');
  const totalPendingAmount = pendingReturns.reduce((sum, ret) => sum + ret.amount, 0);
  
  // Total wealth = remaining capital + approved/reinvested interest only (exclude principal)
  // Principal is not wealth - it's just available capital for withdrawal
  const approvedInterest = returns.filter(ret => 
    ret.return_type === 'interest' && (ret.status === 'approved' || ret.status === 'reinvested')
  );
  const totalApprovedInterest = approvedInterest.reduce((sum, ret) => sum + ret.amount, 0);
  const totalWealth = totalInvested + totalApprovedInterest;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const calculateRetroactiveReturns = (): CalculatedReturn[] => {
    const retroactiveReturns: CalculatedReturn[] = [];
    const today = new Date();
    
    investments.forEach((investment) => {
      const investmentDate = new Date(investment.investment_date);
      const currentDate = new Date(investmentDate);
      
      // Primeiro rendimento em 30 dias após o investimento
      currentDate.setDate(currentDate.getDate() + 30);
      
      while (currentDate <= today) {
        const returnDateStr = currentDate.toISOString().split('T')[0];
        
        // Verificar se já existe um rendimento real para esta data
        const existingReturn = returns.find(r => 
          r.investment_id === investment.id && 
          r.return_date === returnDateStr &&
          r.return_type === 'interest'
        );
        
        if (!existingReturn) {
          retroactiveReturns.push({
            id: `calculated-${investment.id}-${returnDateStr}`,
            investment_id: investment.id,
            return_type: 'interest',
            amount: investment.monthly_return,
            return_date: returnDateStr,
            status: 'calculated',
            is_calculated: true
          });
        }
        
        // Avançar para o próximo mês (30 dias)
        currentDate.setDate(currentDate.getDate() + 30);
      }
    });
    
    return retroactiveReturns.sort((a, b) => new Date(b.return_date).getTime() - new Date(a.return_date).getTime());
  };

  const calculateTotalReturnUpToToday = (): number => {
    const today = new Date();
    const calculatedReturns = calculateRetroactiveReturns();
    
    // Soma apenas rendimentos até a data atual (já calculados retroativamente)
    const totalCalculatedReturns = calculatedReturns.reduce((sum, ret) => sum + ret.amount, 0);
    
    // Soma os rendimentos reais aprovados até hoje
    const totalApprovedReturns = returns
      .filter(ret => new Date(ret.return_date) <= today)
      .reduce((sum, ret) => sum + ret.amount, 0);
    
    return totalCalculatedReturns + totalApprovedReturns;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'active': { label: 'Ativo', variant: 'default' as const },
      'completed': { label: 'Concluído', variant: 'secondary' as const },
      'pending': { label: 'Pendente', variant: 'outline' as const },
      'pending_approval': { label: 'Aguardando Aprovação', variant: 'outline' as const },
      'approved': { label: 'Aprovado', variant: 'default' as const },
      'reinvested': { label: 'Reinvestido', variant: 'default' as const },
      'withdrawn': { label: 'Sacado', variant: 'secondary' as const },
      'calculated': { label: 'Calculado', variant: 'secondary' as const }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  // Empty state for new users
  if (investments.length === 0) {
    return (
      <div className="space-y-6">
        {/* Empty Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <Card className="bg-gradient-card shadow-soft border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
                Total Investido
              </CardTitle>
              <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-lg sm:text-2xl font-bold text-foreground">
                {formatCurrency(0)}
              </div>
              <p className="text-xs text-muted-foreground">
                0 investimentos ativos
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-card shadow-soft border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
                Rendimento Mensal
              </CardTitle>
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-lg sm:text-2xl font-bold text-foreground">
                {formatCurrency(0)}
              </div>
              <p className="text-xs text-muted-foreground">
                Aguardando investimentos
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-card shadow-soft border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total de Patrimônio
              </CardTitle>
              <CircleDollarSign className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                {formatCurrency(0)}
              </div>
              <p className="text-xs text-muted-foreground">
                Investimentos + retornos - saques
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-card shadow-soft border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pendente Aprovação
              </CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                {formatCurrency(0)}
              </div>
              <p className="text-xs text-muted-foreground">
                0 itens pendentes
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for empty state */}
        <Tabs defaultValue="overview" className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto">
            <TabsList className="bg-muted p-1 h-10 sm:h-12 inline-flex min-w-full">
              <TabsTrigger value="overview" className="text-xs sm:text-sm px-3 sm:px-6 whitespace-nowrap">Visão Geral</TabsTrigger>
              <TabsTrigger value="investments" className="text-xs sm:text-sm px-3 sm:px-6 whitespace-nowrap">Investimentos</TabsTrigger>
              <TabsTrigger value="returns" className="text-xs sm:text-sm px-3 sm:px-6 whitespace-nowrap">Rendimentos</TabsTrigger>
              <TabsTrigger value="approvals" className="text-xs sm:text-sm px-3 sm:px-6 whitespace-nowrap">Aprovações</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              <Card className="bg-gradient-card shadow-medium border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Investimentos Ativos
                  </CardTitle>
                  <CardDescription>
                    Seus investimentos atualmente em andamento
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Nenhum investimento ativo
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-card shadow-medium border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Próximos Rendimentos
                  </CardTitle>
                  <CardDescription>
                    Previsão de recebimentos baseada nos investimentos ativos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Nenhum rendimento programado
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="investments" className="space-y-6">
            <Card className="bg-gradient-card shadow-medium border-0">
              <CardHeader>
                <CardTitle>Histórico de Investimentos</CardTitle>
                <CardDescription>
                  Todos os seus investimentos realizados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <DollarSign className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Nenhum investimento realizado</h3>
                  <p className="text-muted-foreground">
                    Os investimentos aparecerão aqui conforme forem sendo realizados.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="returns" className="space-y-6">
            <Card className="bg-gradient-card shadow-medium border-0">
              <CardHeader>
                <CardTitle>Histórico de Rendimentos</CardTitle>
                <CardDescription>
                  Todos os rendimentos recebidos e processados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <TrendingUp className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Nenhum rendimento ainda</h3>
                  <p className="text-muted-foreground">
                    Os rendimentos aparecerão aqui conforme forem sendo processados.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approvals" className="space-y-6">
            <Card className="bg-gradient-card shadow-medium border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-warning" />
                  Aprovações Pendentes
                </CardTitle>
                <CardDescription>
                  Rendimentos aguardando sua decisão de reinvestimento
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Nenhuma aprovação pendente</h3>
                  <p className="text-muted-foreground">
                    Todos os rendimentos foram processados.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Regular dashboard with data
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <Card className="bg-gradient-card shadow-soft border-0 hover:shadow-medium transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Investido
            </CardTitle>
            <DollarSign className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {formatCurrency(totalInvested)}
            </div>
            <p className="text-xs text-muted-foreground">
              {activeInvestments.length} investimentos ativos
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-success shadow-soft border-0 hover:shadow-medium transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-success-foreground">
              Rendimento Mensal
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-success-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success-foreground">
              {formatCurrency(totalMonthlyReturn)}
            </div>
            <p className="text-xs text-success-foreground/80">
              Baseado nos investimentos ativos
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-primary shadow-soft border-0 hover:shadow-medium transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-primary-foreground">
              Total de Patrimônio
            </CardTitle>
            <CircleDollarSign className="h-4 w-4 text-primary-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary-foreground">
              {formatCurrency(totalWealth)}
            </div>
            <p className="text-xs text-primary-foreground/80">
              Investimentos + retornos - saques
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card shadow-soft border-0 hover:shadow-medium transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pendente Aprovação
            </CardTitle>
            <Clock className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {formatCurrency(totalPendingAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {pendingReturns.length} itens pendentes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Principal */}
      <Tabs defaultValue="overview" className="space-y-4 sm:space-y-6">
        <div className="overflow-x-auto">
          <TabsList className="bg-muted p-1 h-10 sm:h-12 inline-flex min-w-full">
            <TabsTrigger value="overview" className="text-xs sm:text-sm px-3 sm:px-6 whitespace-nowrap">Visão Geral</TabsTrigger>
            <TabsTrigger value="investments" className="text-xs sm:text-sm px-3 sm:px-6 whitespace-nowrap">Investimentos</TabsTrigger>
            <TabsTrigger value="returns" className="text-xs sm:text-sm px-3 sm:px-6 whitespace-nowrap">Rendimentos</TabsTrigger>
            <TabsTrigger value="approvals" className="text-xs sm:text-sm px-3 sm:px-6 whitespace-nowrap">
              Aprovações
              {pendingReturns.length > 0 && (
                <Badge className="ml-2 bg-warning text-warning-foreground">
                  {pendingReturns.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            <Card className="bg-gradient-card shadow-medium border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Investimentos Ativos
                </CardTitle>
                <CardDescription>
                  Seus investimentos atualmente em andamento
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activeInvestments.map((investment) => (
                    <div
                      key={investment.id}
                      className="flex items-center justify-between p-4 bg-background/50 rounded-lg border"
                    >
                      <div>
                        <p className="font-medium">{formatCurrency(investment.amount)}</p>
                        <p className="text-sm text-muted-foreground">
                          Investido em {formatDate(investment.investment_date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-success">
                           {(investment.expected_return_rate * 100).toFixed(2)}% a.m.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(investment.monthly_return)}/mês
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card shadow-medium border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Próximos Rendimentos
                </CardTitle>
                <CardDescription>
                  Previsão de recebimentos baseada nos investimentos ativos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {totalMonthlyReturn > 0 && (
                    <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <ArrowUpRight className="h-4 w-4 text-success" />
                        <div>
                          <p className="font-medium">Rendimento Mensal</p>
                          <p className="text-sm text-muted-foreground">Próximo pagamento</p>
                        </div>
                      </div>
                      <p className="font-medium text-success">
                        {formatCurrency(totalMonthlyReturn)}
                      </p>
                    </div>
                  )}
                  
                  {totalMonthlyReturn === 0 && (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        Nenhum rendimento programado
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="investments" className="space-y-6">
          <Card className="bg-gradient-card shadow-medium border-0">
            <CardHeader>
              <CardTitle>Histórico de Investimentos</CardTitle>
              <CardDescription>
                Todos os seus investimentos realizados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {investments.map((investment) => (
                  <div
                    key={investment.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-background/50 rounded-xl border hover:shadow-soft transition-all duration-300 gap-3"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                      <div className="p-2 sm:p-3 bg-primary/10 rounded-full flex-shrink-0">
                        <DollarSign className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-base sm:text-lg">
                          {formatCurrency(investment.amount)}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">
                          Investido em {formatDate(investment.investment_date)}
                        </p>
                      </div>
                    </div>
                    <div className="text-left sm:text-right space-y-2 flex-shrink-0">
                      {getStatusBadge(investment.status)}
                      <div>
                        <p className="font-medium text-success text-sm sm:text-base">
                          {(investment.expected_return_rate * 100).toFixed(2)}% a.m.
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {formatCurrency(investment.monthly_return)}/mês
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="returns" className="space-y-6">
          <Card className="bg-gradient-card shadow-medium border-0">
            <CardHeader>
              <CardTitle>Histórico de Rendimentos</CardTitle>
              <CardDescription>
                Rendimentos calculados retroativamente e processados
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filtros de visualização */}
              <div className="flex gap-4 sm:gap-6 mb-4 sm:mb-6 p-3 sm:p-4 bg-muted/30 rounded-lg overflow-x-auto">
                <div className="flex items-center space-x-2 whitespace-nowrap">
                  <Checkbox
                    id="show-interest"
                    checked={showInterest}
                    onCheckedChange={(checked) => setShowInterest(checked === true)}
                  />
                  <label
                    htmlFor="show-interest"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Juros
                  </label>
                </div>
                <div className="flex items-center space-x-2 whitespace-nowrap">
                  <Checkbox
                    id="show-principal"
                    checked={showPrincipal}
                    onCheckedChange={(checked) => setShowPrincipal(checked === true)}
                  />
                  <label
                    htmlFor="show-principal"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Principal
                  </label>
                </div>
              </div>

              {(() => {
                const calculatedReturns = calculateRetroactiveReturns();
                
                // Separar rendimentos reais dos calculados para exibição
                const realReturns = returns.sort((a, b) => new Date(b.return_date).getTime() - new Date(a.return_date).getTime());
                
                // Filtrar apenas rendimentos REAIS baseado nos checkboxes
                const filteredRealReturns = realReturns.filter(ret => {
                  if (ret.return_type === 'interest' && !showInterest) return false;
                  if (ret.return_type === 'principal' && !showPrincipal) return false;
                  return true;
                });

                // Agrupar apenas rendimentos REAIS por mês para mostrar total mensal
                const groupedByMonth = filteredRealReturns.reduce((acc, ret) => {
                  const monthKey = ret.return_date.substring(0, 7); // YYYY-MM
                  if (!acc[monthKey]) {
                    acc[monthKey] = {
                      month: monthKey,
                      interest: 0,
                      principal: 0,
                      total: 0,
                      returns: []
                    };
                  }
                  
                  if (ret.return_type === 'interest') {
                    acc[monthKey].interest += ret.amount;
                  } else {
                    acc[monthKey].principal += ret.amount;
                  }
                  acc[monthKey].total += ret.amount;
                  acc[monthKey].returns.push(ret);
                  
                  return acc;
                }, {} as Record<string, { month: string; interest: number; principal: number; total: number; returns: Return[] }>);

                const monthlyData = Object.values(groupedByMonth).sort((a, b) => b.month.localeCompare(a.month));
                
                // Total dos rendimentos calculados apenas para exibição informativa
                const totalCalculatedAmount = calculatedReturns
                  .filter(ret => {
                    if (ret.return_type === 'interest' && !showInterest) return false;
                    return true;
                  })
                  .reduce((sum, ret) => sum + ret.amount, 0);
                  
                // Totais reais para os cards
                const totalRealReturns = filteredRealReturns.reduce((sum, ret) => sum + ret.amount, 0);
                
                return (
                  <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-success/5 rounded-lg border border-success/20">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="h-4 w-4 text-success" />
                          <span className="text-sm font-medium text-success">Total Rendimentos Calculados</span>
                        </div>
                        <p className="text-2xl font-bold text-success">{formatCurrency(totalCalculatedAmount)}</p>
                        <p className="text-xs text-muted-foreground">{calculatedReturns.length} rendimentos mensais</p>
                      </div>
                      
                      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                        <div className="flex items-center gap-2 mb-2">
                          <CircleDollarSign className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium text-primary">Valor Atual Estimado</span>
                        </div>
                        <p className="text-2xl font-bold text-primary">
                          {formatCurrency(totalInvested + totalCalculatedAmount)}
                        </p>
                        <p className="text-xs text-muted-foreground">Capital + rendimentos calculados</p>
                      </div>
                    </div>

                    {filteredRealReturns.length === 0 ? (
                      <div className="text-center py-12">
                        <TrendingUp className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">Nenhum rendimento encontrado</h3>
                        <p className="text-muted-foreground">
                          {!showInterest && !showPrincipal 
                            ? 'Selecione pelo menos um tipo de rendimento para visualizar.'
                            : 'Nenhum rendimento encontrado com os filtros selecionados.'
                          }
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Visualização mensal agrupada */}
                        {showInterest && showPrincipal && monthlyData.length > 0 && (
                          <div className="space-y-4">
                            <h4 className="text-lg font-semibold mb-4">Resumo Mensal (Principal + Juros)</h4>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                              {monthlyData.map((monthData) => (
                                <div
                                  key={monthData.month}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-background/50 rounded-lg border hover:shadow-soft transition-all duration-300 gap-3"
                                >
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div className="p-2 bg-primary/10 rounded-full flex-shrink-0">
                                      <Calendar className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="font-semibold truncate">
                                        {new Date(monthData.month + '-01').toLocaleDateString('pt-BR', { 
                                          year: 'numeric', 
                                          month: 'long' 
                                        })}
                                      </p>
                                      <div className="text-xs sm:text-sm text-muted-foreground space-y-1">
                                        {monthData.interest > 0 && (
                                          <div className="truncate">Juros: {formatCurrency(monthData.interest)}</div>
                                        )}
                                        {monthData.principal > 0 && (
                                          <div className="truncate">Principal: {formatCurrency(monthData.principal)}</div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-left sm:text-right flex-shrink-0">
                                    <p className="font-semibold text-base sm:text-lg">{formatCurrency(monthData.total)}</p>
                                    <p className="text-xs sm:text-sm text-muted-foreground">{monthData.returns.length} operações</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Lista detalhada de retornos */}
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold mb-4">
                            Histórico Detalhado
                            {(!showInterest || !showPrincipal) && (
                              <span className="ml-2 text-sm font-normal text-muted-foreground">
                                (Filtrado: {showInterest && 'Juros'}{showInterest && showPrincipal && ' + '}{showPrincipal && 'Principal'})
                              </span>
                            )}
                          </h4>
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {filteredRealReturns.map((returnItem) => (
                              <div
                                key={returnItem.id}
                                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 rounded-xl border hover:shadow-soft transition-all duration-300 gap-3 ${
                                  'is_calculated' in returnItem && returnItem.is_calculated 
                                    ? 'bg-success/5 border-success/20' 
                                    : 'bg-background/50'
                                }`}
                              >
                                <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                                  <div className={`p-2 sm:p-3 rounded-full flex-shrink-0 ${
                                    returnItem.return_type === 'interest' 
                                      ? 'bg-success/10' 
                                      : 'bg-primary/10'
                                  }`}>
                                    {returnItem.return_type === 'interest' ? (
                                      <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 text-success" />
                                    ) : (
                                      <Banknote className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-base sm:text-lg">
                                      {formatCurrency(returnItem.amount)}
                                    </p>
                                    <div className="text-xs sm:text-sm text-muted-foreground">
                                      <div className="truncate">
                                        {returnItem.return_type === 'interest' ? 'Rendimento Mensal' : 'Principal'} - {formatDate(returnItem.return_date)}
                                      </div>
                                      {'is_calculated' in returnItem && returnItem.is_calculated && (
                                        <span className="text-xs text-success">(Calculado)</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-left sm:text-right flex-shrink-0">
                                  {getStatusBadge(returnItem.status)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approvals" className="space-y-6">
          <Card className="bg-gradient-card shadow-medium border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-warning" />
                Aprovações Pendentes
              </CardTitle>
              <CardDescription>
                Rendimentos aguardando sua decisão de reinvestimento
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                return null;
              })()}
              {pendingReturns.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Nenhuma aprovação pendente</h3>
                  <p className="text-muted-foreground">
                    Todos os rendimentos foram processados.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {pendingReturns.map((returnItem) => (
                    <div
                      key={returnItem.id}
                      className="p-4 sm:p-6 bg-background/50 rounded-xl border-2 border-warning/20 hover:shadow-soft transition-all duration-300"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                          <div className={`p-2 sm:p-3 rounded-full flex-shrink-0 ${
                            returnItem.return_type === 'interest' 
                              ? 'bg-success/10' 
                              : 'bg-primary/10'
                          }`}>
                            {returnItem.return_type === 'interest' ? (
                              <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 text-success" />
                            ) : (
                              <Banknote className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-lg sm:text-xl">
                              {formatCurrency(returnItem.amount)}
                            </p>
                            <p className="text-sm sm:text-base text-muted-foreground truncate">
                              {returnItem.return_type === 'interest' ? 'Rendimento de Juros' : 'Retorno do Principal'}
                            </p>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                              Recebido em {formatDate(returnItem.return_date)}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 self-start sm:self-center">
                          {getStatusBadge(returnItem.status)}
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                        <Button
                          variant="default"
                          onClick={() => handleApproveReturn(returnItem.id, 'reinvest')}
                          className="flex-1 w-full sm:w-auto"
                        >
                          <ArrowUpRight className="mr-2 h-4 w-4" />
                          Reinvestir
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleApproveReturn(returnItem.id, 'withdraw')}
                          className="flex-1 w-full sm:w-auto"
                        >
                          <ArrowDownRight className="mr-2 h-4 w-4" />
                          Sacar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InvestmentDashboard;